<template>
  <v-row align="center">
    <v-col
      v-if="isSummaryRoute"
      class="shrink ml-4 ml-md-0"
      cols="2"
      md="auto"
    >
      <fp-btn height="40px" @click="goBackToForm()">
        <v-icon>mdi-arrow-left</v-icon>
      </fp-btn>
    </v-col>
    <v-col
      class="shrink"
      cols="9"
      md="auto"
    >
      <template v-if="store.deal">
        <div class="d-flex">
          <deal-badge
            class="client-autocomplete ph-no-capture"
            :deal="store.deal"
            :small="true"
            :hideTooltip="true"
          />
          <v-tooltip right>
            <template #activator="{on}">
              <fp-btn
                text
                icon
                v-on="on"
                @click="store.deleteSimulationFromDeal()"
              >
                <v-icon>mdi-close-circle-outline</v-icon>
              </fp-btn>
            </template>
            {{ t.UNLOAD_DEAL }}
          </v-tooltip>
        </div>
      </template>
      <v-row v-else class="align-center pl-2">
        <fp-btn :disabled="!store.simulation" @click="openLoadDealModal()">
          <v-icon class="mr-2">mdi-briefcase-upload-outline</v-icon>
          {{ t.LOAD_DEAL }}
        </fp-btn>
        <fp-select
          v-if="$user.isAdminOrVerifier && $user.privileges.isProductAdmin"
          v-model="stagingDBMode"
          :items="[{name: 'Staging', value: true}, {name: 'Produkcja', value: false}]"
          item-name="name"
          item-key="value"
          dense
          :clearable="false"
          class="ml-4"
          @change="onStagingDBModeChange"
        />
      </v-row>
    </v-col>
    <v-spacer />
    <template v-if="isSummaryRoute">
      <v-col v-if="isNewMortgageComparator && $vuetify.breakpoint.mdAndUp" class="shrink">
        <comparator-feedback />
      </v-col>
      <v-col v-if="isNavigationZoomRendered" class="shrink" :class="navigationZoomClass">
        <simulation-toolbar-zoom />
      </v-col>
    </template>
    <v-col v-else class="d-flex justify-end">
      <v-tooltip left>
        <template #activator="{on}">
          <fp-btn v-on="on" :click="() => clear()">
            {{ t.CLEAR }}
          </fp-btn>
        </template>
        {{ t.CLEAR_FORM }}
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import {computed} from 'vue';
import {useFormNavigation} from '@/components/calculator/form/useSimulationForm';
import {useSimulationStore} from '@/components/calculator/services/SimulationStore';
import {useCalculatorVersion} from '@/components/calculator/useCalculatorVersion';
import {useSimulationHorizontalNavigation} from '@/modules/calculator-v25/composables/shared/results/useSimulationHorizontalNavigation';
import {useMortgageRealEstatesStore} from '@/modules/calculator-v25/services/mortgage/MortgageRealEstatesStore';
import {Routes} from '@/router/Routes';
import {ProductType} from '@/commons/enums/ProductType';
import SimulationToolbarZoom from '@/components/calculator/results/toolbar/SimulationToolbarZoom.vue';
import ComparatorFeedback from '@/components/calculator/results/feedback/ComparatorFeedback.vue';
import Deal from '@/models/Deal';
import SimulationDealPicker from '@/components/calculator/results/SimulationDealPicker.vue';
import { useNewDealModal } from '@/composables/useNewDealModal';
import { useInstance } from '@/composables/useInstance';
import {i18nFormatter} from '@/services/StringFormatter';
import AuthService from '@/modules/user/AuthService';
import { hasAccessToNewDealFlow } from '@/modules/user/services/Utils';

const store = useSimulationStore();
const realEstatesStore = useMortgageRealEstatesStore();
const {goBackToForm,} = useFormNavigation();
const {isNewMortgageComparator,} = useCalculatorVersion();
const {isNavigationZoomRendered, navigationZoomClass,} = useSimulationHorizontalNavigation();
const { $user, $modalService, $route, $snackbarService, $i18n, } = useInstance();
const t = $i18n.useCalculatorNavigationComponent;
const tDealPicker = $i18n.useDealPicker;

const stagingDBMode = computed<boolean>({
  get: () => Boolean(store.userInput?.stagingDBMode),
  set: value => {
    if (!store.userInput) {
      return;
    }
    store.userInput.stagingDBMode = value;
  },
});
const newCalculatorSummaryRoutes: string[] = Object.values(ProductType)
  .map(productType => Routes.NEW_CALCULATOR.SUMMARY(productType));
const isSummaryRoute = computed<boolean>(() => newCalculatorSummaryRoutes.includes($route.value.name ?? ''));

const onStagingDBModeChange = async() => {
  await store.createOrUpdateSimulation();
};

const openLoadDealModal = async() => {
  if (hasAccessToNewDealFlow(AuthService.User)) {
    const { openNewDealModal, } = useNewDealModal($modalService);
    const response = await openNewDealModal(undefined, {
      flowTitle: tDealPicker.LOAD_DEAL,
      mode: 'LoadDeal',
      simulationId: store.simulationId || undefined,
      modeProductType: store.productType,
    });

    if (response?.deal) {
      await store.init(store.productType!, store.simulationId, response.deal);
      $snackbarService.openSuccessSnackbar(
        tDealPicker.DEAL_LOADED
      );
    }
    return;
  }

  const deal = await $modalService.open<Deal>(SimulationDealPicker, {
    productType: store.productType,
    simulationId: store.simulationId,
  }) ?? null;
  if (deal) {
    await store.init(store.productType!, deal?.simulationId, deal);
  }
};

const clear = async() => {
  const response = await $modalService.openConfirmModal({
    description: store.deal?.name ? i18nFormatter(
      t.NEW_TRANSLATION_CLEAR_FORM_DESCRIPTION_DEAL,
      {dealName: store.deal?.name,})
      : t.NEW_TRANSLATION_CLEAR_FORM_DESCRIPTION_NO_DEAL,
    title: t.CLEAR_FORM_TITLE,
  }, t.CANCEL, t.CLEAR);
  if (response) {
    realEstatesStore.clear();
    await store.clear();
    await store.createOrUpdateSimulation();
    $snackbarService.openSuccessSnackbar(
      t.CLEAR_SUCCESS
    );
  }
};
</script>
